import React, {useEffect, useState, useRef} from "react";
import Header from "../../components/header/header";
import PageTitle from "../../components/page_title/page_title";
import EmptyPlaceholder from "../../components/empty_placeholder/empty_placeholder";
import Modal from "../../components/modal/modal";
import axiosInstance from "../../AxiosInstance";
import styles from "../../styles/dashboard.module.css";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faFileExport, faUser, faCubes, faSearch} from "@fortawesome/free-solid-svg-icons";
import {useTranslation} from "react-i18next";
import PaginationControls, {getStoredPageSize} from "../../components/pagination/PaginationControls";

const SIDEBAR_INDEX = 7;
const ALL_VALUE = '__all__';

const tableStyle = {
    width: '100%', borderCollapse: 'collapse',
    backgroundColor: '#FFF', borderRadius: '8px',
    overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
};
const thStyle = {
    padding: '10px 16px', textAlign: 'center',
    fontSize: '13px', fontWeight: 600, color: '#64748B'
};
const tdStyle = {
    padding: '10px 16px', fontSize: '13px',
    color: '#334155', textAlign: 'center'
};
const selectStyle = {
    padding: '7px 10px', borderRadius: '6px', border: '1px solid #E2E8F0',
    fontSize: '13px', width: '100%', backgroundColor: '#FFF'
};

export default function HistoricalData() {
    const {t} = useTranslation();
    const project_id = parseInt(localStorage.getItem('project_id'));

    // Selection state
    const [searchText, setSearchText] = useState('');
    const [deviceUsers, setDeviceUsers] = useState([]);
    const [selectedDeviceUser, setSelectedDeviceUser] = useState(null);
    const [devices, setDevices] = useState([]);
    const [selectedDevice, setSelectedDevice] = useState(null);
    const [variables, setVariables] = useState([]);
    const [selectedVariableId, setSelectedVariableId] = useState('');
    const [customVariables, setCustomVariables] = useState([]);
    const [selectedOperationId, setSelectedCustomVariableId] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef(null);

    // Tab
    const [activeTab, setActiveTab] = useState('sensor');

    // Data table
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [pageSize, setPageSize] = useState(getStoredPageSize);

    // Pivoted mode (all variables)
    const [isPivoted, setIsPivoted] = useState(false);
    const [pivotColumns, setPivotColumns] = useState([]); // [{name, unit}]

    // Filters
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    // Export modal
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportEmail, setExportEmail] = useState('');
    const [exportLoading, setExportLoading] = useState(false);
    const [exportSuccess, setExportSuccess] = useState(false);

    // Search DeviceUsers
    useEffect(() => {
        if (searchText.length < 1) {
            setDeviceUsers([]);
            setShowDropdown(false);
            return;
        }
        const timeout = setTimeout(() => {
            axiosInstance.get(`device_user/?project_id=${project_id}&search=${searchText}`)
                .then(res => {
                    const data = Array.isArray(res.data) ? res.data : (res.data.results || []);
                    setDeviceUsers(data);
                    setShowDropdown(true);
                })
                .catch(() => setDeviceUsers([]));
        }, 300);
        return () => clearTimeout(timeout);
    }, [searchText]);

    // Fetch devices when DeviceUser selected
    useEffect(() => {
        if (!selectedDeviceUser) {
            setDevices([]);
            setSelectedDevice(null);
            return;
        }
        axiosInstance.get(`device/?machine=${selectedDeviceUser.id}&project_id=${project_id}`)
            .then(res => {
                const data = Array.isArray(res.data) ? res.data : (res.data.results || []);
                setDevices(data);
                setSelectedDevice(null);
                resetTable();
                if (data.length === 1) setSelectedDevice(data[0]);
            })
            .catch(() => setDevices([]));
    }, [selectedDeviceUser]);

    // Fetch variables when Device selected
    useEffect(() => {
        if (!selectedDevice) {
            setVariables([]);
            setCustomVariables([]);
            resetTable();
            return;
        }
        axiosInstance.get(`variable_data/?device_id=${selectedDevice.id}`)
            .then(res => {
                const data = Array.isArray(res.data) ? res.data : (res.data.results || []);
                setVariables(data);
            })
            .catch(() => setVariables([]));

        axiosInstance.get(`historical_data/device_custom_variables/?device_id=${selectedDevice.id}`)
            .then(res => {
                const data = Array.isArray(res.data) ? res.data : (res.data.results || []);
                setCustomVariables(data);
            })
            .catch(() => setCustomVariables([]));
    }, [selectedDevice]);

    // Fetch historical data when variable selection changes
    useEffect(() => {
        if (activeTab === 'sensor' && selectedVariableId) {
            fetchSensorData(1);
        } else if (activeTab === 'calculated' && selectedOperationId) {
            fetchCalculatedData(1);
        } else {
            resetTable();
        }
    }, [selectedVariableId, selectedOperationId, activeTab]);

    const resetTable = () => {
        setSelectedVariableId('');
        setSelectedCustomVariableId('');
        setRows([]);
        setTotalCount(0);
        setIsPivoted(false);
        setPivotColumns([]);
    };

    const handleResponse = (res) => {
        setRows(res.data.results || []);
        setTotalCount(res.data.count || 0);
        if (res.data.pivoted) {
            setIsPivoted(true);
            setPivotColumns(res.data.columns || []);
        } else {
            setIsPivoted(false);
            setPivotColumns([]);
        }
    };

    const fetchSensorData = (pageNum, pageSizeOverride) => {
        if (!selectedVariableId || !selectedDevice) return;
        setLoading(true);
        setPage(pageNum);
        const ps = pageSizeOverride !== undefined ? pageSizeOverride : pageSize;

        let url;
        if (selectedVariableId === ALL_VALUE) {
            url = `historical_data/sensor_data/?device_id=${selectedDevice.id}&page=${pageNum}&page_size=${ps}`;
        } else {
            url = `historical_data/sensor_data/?variable_data_id=${selectedVariableId}&page=${pageNum}&page_size=${ps}`;
        }
        if (dateFrom) url += `&from_date=${dateFrom}`;
        if (dateTo) url += `&to_date=${dateTo}`;

        axiosInstance.get(url)
            .then(handleResponse)
            .catch(() => { setRows([]); setTotalCount(0); })
            .finally(() => setLoading(false));
    };

    const fetchCalculatedData = (pageNum, pageSizeOverride) => {
        if (!selectedOperationId || !selectedDevice) return;
        setLoading(true);
        setPage(pageNum);
        const ps = pageSizeOverride !== undefined ? pageSizeOverride : pageSize;

        let url;
        if (selectedOperationId === ALL_VALUE) {
            url = `historical_data/calculated_data/?device_id=${selectedDevice.id}&page=${pageNum}&page_size=${ps}`;
        } else {
            url = `historical_data/calculated_data/?operation_id=${selectedOperationId}&page=${pageNum}&page_size=${ps}`;
        }
        if (dateFrom) url += `&from_date=${dateFrom}`;
        if (dateTo) url += `&to_date=${dateTo}`;

        axiosInstance.get(url)
            .then(handleResponse)
            .catch(() => { setRows([]); setTotalCount(0); })
            .finally(() => setLoading(false));
    };

    const handleFilter = () => {
        if (activeTab === 'sensor') fetchSensorData(1);
        else fetchCalculatedData(1);
    };

    const handleClearFilters = () => {
        setDateFrom('');
        setDateTo('');
        setTimeout(() => {
            if (activeTab === 'sensor') fetchSensorData(1);
            else fetchCalculatedData(1);
        }, 0);
    };

    const handlePageChange = (newPage) => {
        if (activeTab === 'sensor') fetchSensorData(newPage);
        else fetchCalculatedData(newPage);
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        if (activeTab === 'sensor') fetchSensorData(1, newSize);
        else fetchCalculatedData(1, newSize);
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setSelectedVariableId('');
        setSelectedCustomVariableId('');
        setRows([]);
        setTotalCount(0);
        setPage(1);
        setIsPivoted(false);
        setPivotColumns([]);
    };

    const handleExport = () => {
        if (!exportEmail) return;
        setExportLoading(true);

        const isAll = activeTab === 'sensor'
            ? selectedVariableId === ALL_VALUE
            : selectedOperationId === ALL_VALUE;

        const payload = {
            email: exportEmail,
            type: activeTab,
            from_date: dateFrom,
            to_date: dateTo,
        };

        if (isAll) {
            payload.device_id = selectedDevice.id;
        } else {
            payload.variable_id = activeTab === 'sensor' ? selectedVariableId : selectedOperationId;
        }

        axiosInstance.post('historical_data/request_export/', payload)
            .then(() => {
                setExportSuccess(true);
                setTimeout(() => {
                    setShowExportModal(false);
                    setExportSuccess(false);
                    setExportEmail('');
                }, 2000);
            })
            .catch(() => alert(t('historical.exportFailed')))
            .finally(() => setExportLoading(false));
    };

    // Close dropdown on outside click
    useEffect(() => {
        const handler = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const totalPages = Math.ceil(totalCount / pageSize);
    const hasSelection = activeTab === 'sensor' ? !!selectedVariableId : !!selectedOperationId;

    // Render table based on pivoted vs flat mode
    const renderTable = () => {
        if (isPivoted && pivotColumns.length > 0) {
            // Pivoted: variable names as column headers
            return (
                <table style={tableStyle}>
                    <thead>
                        <tr style={{backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0'}}>
                            <th style={thStyle}>Timestamp</th>
                            {pivotColumns.map((col, i) => (
                                <th key={i} style={thStyle}>
                                    {col.name}
                                    {col.unit && <span style={{fontWeight: 400, color: '#94A3B8'}}> ({col.unit})</span>}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, index) => (
                            <tr key={index} style={{borderBottom: '1px solid #F1F5F9'}}>
                                <td style={{...tdStyle, color: '#94A3B8'}}>{row.timestamp}</td>
                                {pivotColumns.map((col, i) => (
                                    <td key={i} style={{...tdStyle, fontWeight: 500}}>
                                        {row[col.name] !== undefined ? row[col.name] : '-'}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            );
        }

        // Flat: single variable mode
        return (
            <table style={tableStyle}>
                <thead>
                    <tr style={{backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0'}}>
                        <th style={thStyle}>Timestamp</th>
                        <th style={thStyle}>Variable</th>
                        {activeTab === 'sensor' && <th style={thStyle}>Device</th>}
                        <th style={thStyle}>Value</th>
                        {activeTab === 'sensor' && <th style={thStyle}>Unit</th>}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, index) => (
                        <tr key={index} style={{borderBottom: '1px solid #F1F5F9'}}>
                            <td style={{...tdStyle, color: '#94A3B8'}}>{row.timestamp}</td>
                            <td style={tdStyle}>{row.variable}</td>
                            {activeTab === 'sensor' && <td style={tdStyle}>{row.device}</td>}
                            <td style={{...tdStyle, fontWeight: 500}}>{row.value}</td>
                            {activeTab === 'sensor' && <td style={{...tdStyle, color: '#94A3B8'}}>{row.unit}</td>}
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    return (
        <div className={styles.dashboard_container}>
            <Header sidebarIndex={SIDEBAR_INDEX}/>

            <div className={'mx-5 mb-4'}>
                <PageTitle title={t('historical.title')}/>

                {/* Chained Selects */}
                <div style={{display: 'flex', gap: '16px', marginTop: '16px', flexWrap: 'wrap', alignItems: 'flex-end'}}>

                    {/* Search DeviceUser */}
                    <div style={{position: 'relative', minWidth: '250px'}} ref={dropdownRef}>
                        <label style={{fontSize: '12px', color: '#64748B', marginBottom: '4px', display: 'block'}}>{t('historical.assetPerson')}</label>
                        <div style={{display: 'flex', alignItems: 'center', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '6px 10px', backgroundColor: '#FFF'}}>
                            <FontAwesomeIcon icon={faSearch} style={{color: '#94A3B8', marginRight: '8px', fontSize: '13px'}}/>
                            <input
                                type="text"
                                value={selectedDeviceUser ? selectedDeviceUser.name : searchText}
                                onChange={(e) => {
                                    setSearchText(e.target.value);
                                    if (selectedDeviceUser) {
                                        setSelectedDeviceUser(null);
                                        setSelectedDevice(null);
                                        resetTable();
                                    }
                                }}
                                placeholder="Search..."
                                style={{border: 'none', outline: 'none', fontSize: '13px', width: '100%'}}
                            />
                        </div>
                        {showDropdown && deviceUsers.length > 0 && (
                            <div style={{
                                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                                backgroundColor: '#FFF', border: '1px solid #E2E8F0', borderRadius: '6px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: '200px', overflowY: 'auto', marginTop: '4px'
                            }}>
                                {deviceUsers.map(du => (
                                    <div key={du.id} onClick={() => {
                                        setSelectedDeviceUser(du);
                                        setShowDropdown(false);
                                        setSearchText('');
                                    }} style={{
                                        padding: '8px 12px', cursor: 'pointer', fontSize: '13px',
                                        display: 'flex', alignItems: 'center', gap: '8px',
                                        borderBottom: '1px solid #F1F5F9'
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#F8FAFC'}
                                    onMouseLeave={e => e.currentTarget.style.backgroundColor = '#FFF'}
                                    >
                                        <FontAwesomeIcon icon={du.is_human ? faUser : faCubes} style={{color: '#94A3B8', width: '14px'}}/>
                                        <span>{du.name}</span>
                                        <span style={{fontSize: '11px', color: '#94A3B8', marginLeft: 'auto'}}>
                                            {du.is_human ? 'Person' : 'Machine'}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Select Device */}
                    <div style={{minWidth: '200px'}}>
                        <label style={{fontSize: '12px', color: '#64748B', marginBottom: '4px', display: 'block'}}>{t('historical.device')}</label>
                        <select
                            value={selectedDevice?.id || ''}
                            onChange={e => {
                                const dev = devices.find(d => d.id === parseInt(e.target.value));
                                setSelectedDevice(dev || null);
                                resetTable();
                            }}
                            disabled={!selectedDeviceUser || devices.length === 0}
                            style={selectStyle}
                        >
                            <option value="">Select device...</option>
                            {devices.map(d => (
                                <option key={d.id} value={d.id}>{d.name || `Device #${d.id}`}</option>
                            ))}
                        </select>
                    </div>

                    {/* Select Variable */}
                    <div style={{minWidth: '220px'}}>
                        <label style={{fontSize: '12px', color: '#64748B', marginBottom: '4px', display: 'block'}}>
                            {activeTab === 'sensor' ? t('historical.sensorVariable') : t('historical.calculatedVariable')}
                        </label>
                        {activeTab === 'sensor' ? (
                            <select
                                value={selectedVariableId}
                                onChange={e => {
                                    setSelectedVariableId(e.target.value);
                                    setRows([]);
                                    setPage(1);
                                    setIsPivoted(false);
                                    setPivotColumns([]);
                                }}
                                disabled={!selectedDevice || variables.length === 0}
                                style={selectStyle}
                            >
                                <option value="">Select variable...</option>
                                <option value={ALL_VALUE}>All Variables</option>
                                {variables.map(v => (
                                    <option key={v.id} value={v.id}>{v.variable?.name || v.name || `Var #${v.id}`}</option>
                                ))}
                            </select>
                        ) : (
                            <select
                                value={selectedOperationId}
                                onChange={e => {
                                    setSelectedCustomVariableId(e.target.value);
                                    setRows([]);
                                    setPage(1);
                                    setIsPivoted(false);
                                    setPivotColumns([]);
                                }}
                                disabled={!selectedDevice || customVariables.length === 0}
                                style={selectStyle}
                            >
                                <option value="">Select variable...</option>
                                <option value={ALL_VALUE}>All Variables</option>
                                {customVariables.map(cv => (
                                    <option key={cv.id} value={cv.id}>{cv.name}</option>
                                ))}
                            </select>
                        )}
                    </div>
                </div>

                {/* Tabs */}
                <div style={{display: 'flex', gap: '0', marginTop: '20px', borderBottom: '2px solid #E2E8F0'}}>
                    <div
                        onClick={() => handleTabChange('sensor')}
                        style={{
                            padding: '10px 24px', cursor: 'pointer', fontSize: '14px', fontWeight: 500,
                            color: activeTab === 'sensor' ? '#4F46E5' : '#64748B',
                            borderBottom: activeTab === 'sensor' ? '2px solid #4F46E5' : '2px solid transparent',
                            marginBottom: '-2px',
                        }}
                    >
                        {t('historical.sensorData')}
                    </div>
                    <div
                        onClick={() => handleTabChange('calculated')}
                        style={{
                            padding: '10px 24px', cursor: 'pointer', fontSize: '14px', fontWeight: 500,
                            color: activeTab === 'calculated' ? '#4F46E5' : '#64748B',
                            borderBottom: activeTab === 'calculated' ? '2px solid #4F46E5' : '2px solid transparent',
                            marginBottom: '-2px',
                        }}
                    >
                        {t('historical.calculatedData')}
                    </div>
                </div>

                {/* Date Filters + Export */}
                {hasSelection && (
                    <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginTop: '16px', flexWrap: 'wrap'}}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                            <label style={{fontSize: '13px', color: '#64748B'}}>{t('common.from')}</label>
                            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                                style={{padding: '6px 10px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '13px'}}/>
                        </div>
                        <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                            <label style={{fontSize: '13px', color: '#64748B'}}>{t('common.to')}</label>
                            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                                style={{padding: '6px 10px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '13px'}}/>
                        </div>
                        <button onClick={handleFilter}
                            style={{padding: '6px 16px', borderRadius: '6px', backgroundColor: '#4F46E5', color: '#FFF',
                                border: 'none', fontSize: '13px', cursor: 'pointer'}}>
                            {t('common.filter')}
                        </button>
                        {(dateFrom || dateTo) && (
                            <button onClick={handleClearFilters}
                                style={{padding: '6px 16px', borderRadius: '6px', backgroundColor: '#F1F5F9', color: '#64748B',
                                    border: '1px solid #E2E8F0', fontSize: '13px', cursor: 'pointer'}}>
                                {t('common.clear')}
                            </button>
                        )}
                        <button onClick={() => { setExportSuccess(false); setShowExportModal(true); }}
                            style={{padding: '6px 16px', borderRadius: '6px', backgroundColor: '#10B981', color: '#FFF',
                                border: 'none', fontSize: '13px', cursor: 'pointer', marginLeft: 'auto'}}>
                            <FontAwesomeIcon icon={faFileExport} className={'me-2'}/>
                            {t('historical.exportExcel')}
                        </button>
                        <span style={{fontSize: '12px', color: '#94A3B8'}}>{totalCount.toLocaleString()} results</span>
                    </div>
                )}

                {/* Data Table */}
                <div style={{marginTop: '16px', overflowX: 'auto'}}>
                    {loading ? (
                        <div style={{textAlign: 'center', padding: '40px', color: '#64748B'}}>Loading...</div>
                    ) : !hasSelection ? (
                        <div style={{textAlign: 'center', padding: '60px'}}>
                            <EmptyPlaceholder message={'Select an asset, device, and variable to view historical data'}/>
                        </div>
                    ) : rows.length > 0 ? (
                        <>
                            {renderTable()}

                            <PaginationControls
                                page={page}
                                totalPages={totalPages}
                                pageSize={pageSize}
                                onPageChange={handlePageChange}
                                onPageSizeChange={handlePageSizeChange}
                                totalCount={totalCount}
                            />
                        </>
                    ) : (
                        <div style={{textAlign: 'center', padding: '60px'}}>
                            <EmptyPlaceholder message={'No data found for the selected filters'}/>
                        </div>
                    )}
                </div>
            </div>

            {/* Export Modal */}
            <Modal show={showExportModal} setter={setShowExportModal}
                name={'Export Historical Data'}
                buttonName={exportSuccess ? 'Done!' : (exportLoading ? 'Sending...' : 'Request Export')}
                onClick={exportSuccess ? () => setShowExportModal(false) : handleExport}>
                {exportSuccess ? (
                    <div style={{textAlign: 'center', padding: '20px'}}>
                        <div style={{fontSize: '16px', fontWeight: 500, color: '#10B981', marginBottom: '8px'}}>Export Queued!</div>
                        <div style={{fontSize: '13px', color: '#64748B'}}>You will receive an email with the download link shortly.</div>
                    </div>
                ) : (
                    <div>
                        <div style={{fontSize: '13px', color: '#64748B', marginBottom: '16px'}}>
                            Large datasets may take several minutes to generate. Enter your email and we'll send you a download link when it's ready.
                        </div>
                        <label style={{fontSize: '12px', color: '#64748B', marginBottom: '4px', display: 'block'}}>Email</label>
                        <input
                            type="email"
                            value={exportEmail}
                            onChange={e => setExportEmail(e.target.value)}
                            placeholder="your@email.com"
                            style={{
                                padding: '8px 12px', borderRadius: '6px', border: '1px solid #E2E8F0',
                                fontSize: '13px', width: '100%'
                            }}
                        />
                    </div>
                )}
            </Modal>
        </div>
    );
}
